import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react'
import { useField, useForm } from 'react-final-form'
import { requiredAddress } from 'forms/validators'
import { getIsCustomerAddressDetailed } from 'helpers/address'
import { checkFieldError } from 'helpers/form'
import { useActions } from 'hooks/useActions'
import { useTypedSelector } from 'hooks/useTypedSelector'
import debounce from 'lodash/debounce'
import noop from 'lodash/noop'
import { ADDRESS_SUGGESTION_ERROR, TAddress, useGetAddressMutation, useGetCompanyMutation } from 'modules/customer/api'
import { isCompanyLegal, isSoleLegal } from 'modules/customer/helpers'
import { DataSourceType } from 'modules/order/types/orderGuestCreateTypes'
import IconInformation from 'remixicon/icons/System/information-line.svg?react'
import { isActiveManualEntryAddressSelector, isIpSelector, isLegalSelector } from 'store/customerAdd/selectors'
import { CustomerAddLocation } from 'store/customerAdd/types'

import { DEBOUNCE_TIME } from './constants'
import CustomerDadataDisableAdd from './CustomerDadataDisableAdd'
import { FieldCustomerDadataDisableAddProps } from './types'

const FieldCustomerDadataDisableAdd: FC<FieldCustomerDadataDisableAddProps> = (props) => {
  const { name, validate, storeKey } = props
  const [getCompany, { data: company, isLoading: isCompanyLoading, error: companySuggestionError }] =
    useGetCompanyMutation()
  const [getAddress, { data: addressData, isLoading: isAddressLoading, error: addressSuggestionError }] =
    useGetAddressMutation()

  const form = useForm()
  const debouncedGetCompany = useMemo(() => debounce(getCompany, DEBOUNCE_TIME), [getCompany])

  const [companySuggestionErrorMessage, setCompanySuggestionErrorMessage] = useState('')
  const { errorAddress, searchAddressValue, isDadataCustomerDisable, isDadataAddressDisable, selectedOptions } =
    useTypedSelector((state) => state.customerAddReducer[storeKey])
  const isIp = useTypedSelector((state) => isIpSelector(state, storeKey))
  const isLegal = useTypedSelector((state) => isLegalSelector(state, storeKey))
  const isActiveManualEntryAddress = useTypedSelector((state) => isActiveManualEntryAddressSelector(state, storeKey))

  const {
    setCustomerAddSearchAddressValue,
    setCustomerAddSelectedOptions,
    setCustomerAddIsDadataCustomerDisable,
    setCustomerAddIsDadataAddressDisable,
    setCustomerAddErrorAddress,
    clearCustomerAddKeyInfo
  } = useActions()

  const { meta: companyNameMeta } = useField<string>(name ? `${name}.company.name` : 'company.name', {
    validate
  })
  const { input: companyDataSourceInput } = useField<string>(name ? `${name}.company.dataSource` : 'company.dataSource')
  const { input: companyInput } = useField<{ inn: string }>(name ? `${name}.company` : 'company')
  const { input: companyLegalAddressInput, meta: companyLegalAddressMeta } = useField<string>(
    name ? `${name}.company.legalAddress` : 'company.legalAddress',
    {
      validate: !isDadataCustomerDisable && !isDadataAddressDisable ? requiredAddress : noop
    }
  )

  const errorCompany = useMemo(() => {
    if (companySuggestionErrorMessage) {
      return companySuggestionErrorMessage
    }

    return typeof checkFieldError(companyNameMeta) !== 'boolean' && checkFieldError(companyNameMeta)
      ? String(checkFieldError(companyNameMeta))
      : undefined
  }, [companySuggestionErrorMessage, companyNameMeta])

  useEffect(() => {
    // @ts-ignore
    const message = companySuggestionError?.message || ''
    if (message) {
      setCompanySuggestionErrorMessage(message)

      return
    }
    setCompanySuggestionErrorMessage('')
    // eslint-disable-next-line
  }, [companySuggestionError])

  useEffect(() => {
    // @ts-ignore
    const message = addressSuggestionError?.message || ''
    if (message) {
      setCustomerAddErrorAddress({ key: storeKey, value: message })

      return
    }
    if (errorAddress === ADDRESS_SUGGESTION_ERROR) {
      setCustomerAddErrorAddress({ key: storeKey, value: '' })
    }
    // eslint-disable-next-line
  }, [addressSuggestionError])

  /* Проверка на ручной ввод данных, вынесена тк информация о недоступности приходить позже выбранной компании */
  useEffect(() => {
    const { inn } = companyInput.value
    const isIpOption = isSoleLegal(inn)
    const isActiveManualEntryAddress = (isIpOption && isDadataAddressDisable) || isDadataCustomerDisable

    const dataSourceActive = isActiveManualEntryAddress ? DataSourceType.user : DataSourceType.auto
    const dataSource = !isDadataCustomerDisable ? dataSourceActive : DataSourceType.user

    if (isCompanyLegal(inn)) {
      companyDataSourceInput.onChange(dataSource)
    }

    if (isIpOption) {
      companyDataSourceInput.onChange(dataSource)
    }
  }, [companyDataSourceInput, companyInput.value, isDadataAddressDisable, isDadataCustomerDisable])

  const handleSetSelectedOptions = useCallback(
    (selectedOption: CustomerAddLocation['selectedOptions']) => {
      if (!selectedOption) {
        form.change('name', '')

        return
      }
      form.change('name', selectedOption.value)
      let option
      const { value, address, inn, ...rest } = selectedOption
      const isIpOption = isSoleLegal(inn)
      const isActiveManualEntryAddress = (isIpOption && isDadataAddressDisable) || isDadataCustomerDisable

      const legalAddress = isActiveManualEntryAddress ? undefined : address

      if (isCompanyLegal(inn)) {
        option = { ...rest, name: value, inn }

        companyInput.onChange(option)
        companyLegalAddressInput.onChange(legalAddress)
      }

      if (isIpOption) {
        option = { ...rest, name: value, inn }

        companyInput.onChange(option)
        setCustomerAddErrorAddress({ key: storeKey, value: 'Уточните адрес' })
      }

      setCustomerAddSelectedOptions({ key: storeKey, value: selectedOption })
    },
    // eslint-disable-next-line
    [
      setCustomerAddSelectedOptions,
      setCustomerAddErrorAddress,
      companyDataSourceInput,
      companyInput,
      companyLegalAddressInput,
      isDadataAddressDisable,
      isDadataCustomerDisable
    ]
  )

  const handleSetSelectedAddressOption = useCallback(
    (address: React.SetStateAction<TAddress | null>) => {
      if (address !== null && typeof address !== 'function') {
        const isDetailedAddressOption = getIsCustomerAddressDetailed(address && address?.fiasLevel)

        if (isDetailedAddressOption) {
          setCustomerAddErrorAddress({ key: storeKey, value: '' })
          companyLegalAddressInput.onChange(address.value)
        } else {
          setCustomerAddErrorAddress({ key: storeKey, value: 'Уточните адрес' })
          companyLegalAddressInput.onChange('')
        }
      }
    },
    [storeKey, setCustomerAddErrorAddress, companyLegalAddressInput]
  )

  useEffect(() => {
    if (company !== null) {
      setCustomerAddIsDadataCustomerDisable({ key: storeKey, value: false })
    } else {
      setCustomerAddIsDadataCustomerDisable({ key: storeKey, value: true })
    }
  }, [storeKey, setCustomerAddIsDadataCustomerDisable, company])

  const errorFieldAddress = useMemo(() => {
    const error = checkFieldError(companyLegalAddressMeta)

    return typeof error !== 'boolean' ? error : ''
  }, [companyLegalAddressMeta])

  useEffect(
    () => () => {
      clearCustomerAddKeyInfo({ key: storeKey })
    },
    // eslint-disable-next-line
    []
  )

  return (
    <>
      {isDadataCustomerDisable && (
        <div className="mt-4 grid grid-cols-[auto_1fr] items-start gap-x-4 rounded bg-surface-variant p-3">
          <IconInformation className="text-on-surface-variant" containerSize="nm" size="xs" />
          <div>
            К&nbsp;сожалению, поиск по&nbsp;названию компании или ИНН временно не&nbsp;доступен. Заполните поля вручную.
          </div>
        </div>
      )}
      <CustomerDadataDisableAdd
        address={addressData}
        company={company}
        debouncedGetCompany={debouncedGetCompany}
        errorAddress={errorFieldAddress || errorAddress}
        errorCompany={errorCompany}
        getAddress={getAddress}
        isActiveManualEntryAdress={isActiveManualEntryAddress}
        isAddressLoading={isAddressLoading}
        isCompanyLoading={isCompanyLoading}
        isDadataAddressDisable={isDadataAddressDisable}
        isDadataCustomerDisable={isDadataCustomerDisable}
        isIp={isIp}
        isLegal={isLegal}
        name={name}
        searchAddressValue={searchAddressValue}
        selectedOptions={selectedOptions}
        setErrorAddress={(value) => setCustomerAddErrorAddress({ key: storeKey, value })}
        setErrorCompany={noop}
        setIsDadataAddressDisable={(value) => setCustomerAddIsDadataAddressDisable({ key: storeKey, value })}
        setSearchAddressValue={(value) => setCustomerAddSearchAddressValue({ key: storeKey, value })}
        setSelectedAddressOption={handleSetSelectedAddressOption}
        setSelectedOptions={handleSetSelectedOptions}
      />
    </>
  )
}

export default memo(FieldCustomerDadataDisableAdd)
